import React, { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from "@material-tailwind/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface PlanDetailsPageProps {
  open: boolean;
  handleClose?: () => void;
  data: {
    id?: number;
    name?: string;
    amount?: string;
    interval?: string;
    createdAt?: string;
    updatedAt?: string;
    productId?: string;
    priceId?: string;
    currency?: string;
    trial_periode?: number;
    Features?: Array<{
      id: number;
      description: string;
      PlanFeatures: {
        value: string;
      };
    }>;
  } | null;
}

const PlanDetailsPage: React.FC<PlanDetailsPageProps> = ({ open, handleClose, data }) => {
  const [activeTab, setActiveTab] = useState("basic");

  const intervalTranslations: Record<string, string> = {
    day: "Jour",
    week: "Semaine",
    month: "Mois",
    year: "Année",
  };

  const fieldIcons = {
    name: "las la-box",
    amount: "las la-euro-sign",
    interval: "las la-clock",
    createdAt: "las la-calendar-plus",
    productId: "las la-tag",
    features: "las la-list",
  };

  // Tab data
  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Fonctionnalités", value: "features" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => {}}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Forfait
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "Nom du forfait", value: data?.name, icon: fieldIcons.name },
                {
                  label: "Prix",
                  value: data?.amount ? `${data.amount} ${data.currency?.toUpperCase()}` : "N/A",
                  icon: fieldIcons.amount,
                },
                {
                  label: "Période",
                  value: data?.interval ? intervalTranslations[data.interval] : "N/A",
                  icon: fieldIcons.interval,
                },
                {
                  label: "Créé le",
                  value: data?.createdAt ? new Date(data.createdAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.createdAt,
                },
                { label: "ID Produit", value: data?.productId, icon: fieldIcons.productId },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>

            {/* Features Tab */}
            <TabPanel value="features" className="space-y-4">
              {data?.Features?.length ? (
                data.Features.map((feature, index) => (
                  <div
                    key={index}
                    className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex items-center space-x-2">
                        <i className={`${fieldIcons.features} text-xl text-blue-500 dark:text-blue-400`}></i>
                        <Label className="font-semibold text-gray-700 dark:text-gray-200">
                          {feature.description} :
                        </Label>
                      </div>
                      <Typography className="font-medium text-gray-900 dark:text-white">
                        {feature.PlanFeatures.value || "N/A"}
                      </Typography>
                    </div>
                  </div>
                ))
              ) : (
                <Typography className="font-medium text-gray-900 dark:text-white">
                  Aucune fonctionnalité disponible.
                </Typography>
              )}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default PlanDetailsPage;
